import {
  ArrowsClockwise, ArrowsSplit, Bell, Brain, CalendarPlus, Cards, ChatCircle,
  CheckSquare, Circle, Clock, Database, Envelope, Flask, Gauge, Globe, Lightning,
  Play, Question, SignOut, Target, UserPlus, ArrowBendUpLeft,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react'
import { LinkedInBrandIcon } from '@/components/icons/LinkedInBrandIcon'
import { describeAction } from '@/lib/workflowActions'

/**
 * Per-node-type icon + human label for the READ-ONLY renderings of a workflow
 * (the campaign detail flowchart and the approval review modal). Monochrome
 * on purpose: on those surfaces colour is reserved for status, so node type is
 * conveyed by icon + label only. The builder keeps its own coloured catalog in
 * pages/workflows/nodeCatalog.ts.
 */
export const NODE_META: Record<string, { kind: string; Icon: PhosphorIcon }> = {
  trigger: { kind: 'Trigger', Icon: Play },
  sendEmail: { kind: 'Send Email', Icon: Envelope },
  delay: { kind: 'Delay', Icon: Clock },
  condition: { kind: 'Condition', Icon: Question },
  action: { kind: 'Action', Icon: Lightning },
  abTest: { kind: 'A/B Test', Icon: Flask },
  goal: { kind: 'Goal', Icon: Target },
  generateDeck: { kind: 'Collateral', Icon: Cards },
  enrichContacts: { kind: 'Enrich', Icon: Database },
  internalAlert: { kind: 'Team Alert', Icon: Bell },
  exitWorkflow: { kind: 'Exit', Icon: SignOut },
  createTask: { kind: 'Create Task', Icon: CheckSquare },
  httpRequest: { kind: 'HTTP Request', Icon: Globe },
  rateLimit: { kind: 'Rate Limit', Icon: Gauge },
  branchByField: { kind: 'Branch', Icon: ArrowsSplit },
  linkedInAction: { kind: 'LinkedIn', Icon: LinkedInBrandIcon },
  smsMessage: { kind: 'SMS', Icon: ChatCircle },
  assignOwner: { kind: 'Assign Owner', Icon: UserPlus },
  aiDecision: { kind: 'AI Decision', Icon: Brain },
  crmSync: { kind: 'CRM Sync', Icon: ArrowsClockwise },
  scheduleMeeting: { kind: 'Schedule Meeting', Icon: CalendarPlus },
  autoReply: { kind: 'Auto Reply', Icon: ArrowBendUpLeft },
}

export const FALLBACK_META: { kind: string; Icon: PhosphorIcon } = { kind: 'Step', Icon: Circle }

/** "branchByField" -> "Branch By Field". */
export function humanizeType(type: string): string {
  return type.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()).trim()
}

/** The display name of a node type: catalog kind, else humanized type. A
 *  delay in "For Event" mode reads as "Wait for Event", as in the builder. */
export function nodeKind(type: string | undefined, data?: Record<string, any> | null): string {
  if (!type) return FALLBACK_META.kind
  if (type === 'delay' && data?.delay_mode === 'wait_for_event') return 'Wait for Event'
  return NODE_META[type]?.kind ?? humanizeType(type)
}

// Human label for the event a "For Event" delay waits on.
const DELAY_EVENT_LABELS: Record<string, string> = {
  email_opened: 'email opened',
  email_clicked: 'link clicked',
  email_replied: 'email replied',
}

/** A short subtitle for a node, pulled from its config. Kept minimal so a
 *  read-only canvas stays clean. */
export function subtitleOf(type: string, data: Record<string, any>): string | undefined {
  if (type === 'sendEmail' && data?.subject) return `Subject: ${data.subject}`
  if (type === 'delay') {
    if (data?.delay_mode === 'wait_for_event') {
      const ev = DELAY_EVENT_LABELS[data?.event_type] || 'event'
      const t = data?.event_timeout_value ?? 3
      const u = data?.event_timeout_unit || 'days'
      return `Wait for ${ev} · ${t} ${u} timeout`
    }
    if (data?.delay_mode === 'wait_until_time') {
      return `Wait until ${data?.wait_until_time || '09:00'}`
    }
    if (data?.delay_value != null) return `Wait ${data.delay_value} ${data.delay_unit || ''}`.trim()
    return undefined
  }
  if (type === 'condition' && data?.condition_type) return humanizeType(String(data.condition_type))
  // Shares the builder's describeAction so the two surfaces never diverge.
  if (type === 'action') return describeAction(data)
  return undefined
}

/** Pretty label for a branch edge, from its sourceHandle. */
export function edgeLabel(handle?: string | null): string | undefined {
  if (!handle) return undefined
  const h = handle.toLowerCase()
  if (h === 'yes' || h === 'true') return 'Yes'
  if (h === 'no' || h === 'false') return 'No'
  if (h === 'event') return 'Event'
  if (h === 'timeout') return 'Timeout'
  return handle
}

export interface NodeSummaryRow {
  type: string
  kind: string
  count: number
}

/**
 * Counts a workflow's nodes by type for the approval review modal's summary
 * chips ("3 Send Email, 2 Delay, 1 Condition"). Sorted by count descending,
 * then by kind so equal counts read in a stable order. Non-object entries and
 * nodes without a type are ignored.
 */
export function nodeSummary(nodes: ReadonlyArray<{ type?: string | null } | null | undefined>): NodeSummaryRow[] {
  const counts = new Map<string, number>()
  for (const n of nodes) {
    const type = n?.type
    if (!type) continue
    counts.set(type, (counts.get(type) ?? 0) + 1)
  }
  return Array.from(counts.entries())
    .map(([type, count]) => ({ type, kind: nodeKind(type), count }))
    .sort((a, b) => b.count - a.count || a.kind.localeCompare(b.kind))
}
